import {
  ChevronLeft,
  Headset,
  Hourglass,
  ImagePlus,
  LifeBuoy,
  Mail,
  Maximize2,
  MessageCircleQuestion,
  Phone,
  Plus,
  Smile,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/envelope';
import { putToS3 } from '../api/comms';
import { EMOJI } from '../lib/emoji';
import {
  cancelHelpHandoff,
  deleteHelpThread,
  getHelpStatus,
  getHelpSuggestions,
  getHelpThread,
  HELP_MAX_ATTACHMENTS,
  HELP_UPLOAD_ACCEPT,
  HELP_UPLOAD_MAX_BYTES,
  listHelpThreads,
  pollHelpThread,
  presignHelpUpload,
  raiseHelpTicket,
  requestHelpHandoff,
  sendHelpChat,
  type HelpHandoff,
  type HelpSource,
  type HelpSupport,
} from './api';
import { renderMarkdown } from '../lib/markdown';
import { useDismissOnOutside } from '../lib/useDismiss';
import { moduleIcon } from './moduleIcon';
import type { HelpPageSnapshot } from './pageContext';
import {
  lastStoredId,
  mergeMessages,
  pendingMessage,
  relativeTime,
  type HelpAttachment,
  type HelpMessage,
  type HelpThread,
} from './threads';
import './helpchat.css';

/**
 * The chat surface, host-agnostic: mounted in the console's launcher slide-over
 * AND full-bleed in the /help-widget iframe the dashboard embeds. Answers are
 * NOT streamed (the AI seam is blocking), so the waiting state narrates the
 * stages honestly instead of pretending to type.
 */
interface Suggestion {
  route: string | null;
  screen: string | null;
  questions: string[];
}
const NO_SUGGESTION: Suggestion = { route: null, screen: null, questions: [] };

const STAGES: [number, string][] = [
  [0, 'Thinking…'],
  [2000, 'Searching the handbook…'],
  [6000, 'Reading articles…'],
  [14000, 'Almost there…'],
];

/** Where the ticket button is in its life — one per conversation, server-capped. */
type TicketState =
  | { state: 'idle' }
  | { state: 'sending' }
  | { state: 'sent'; acknowledgedTo: string | null }
  | { state: 'error'; message: string };

/** How long an unclaimed request waits — WAIT_MS in services/help/handoffs.js. */
const HANDOFF_WAIT_MINUTES = 30;
/** A failed poll is retried, slower — it must never silently stop listening. */
const POLL_RETRY_MS = 10_000;

/** "Mon 9:00 AM" in the operator's own clock, which is the one they read. */
const opensAt = (iso: string) =>
  new Date(iso).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' });

/**
 * "Talk to a person": what it does, and whether the desk is open. Out of hours
 * the button still works — the line above it says plainly that the request may
 * wait, because one that sits unanswered with no warning is worse than none.
 * The urgency box is the operator's own say in how soon; it promises nothing
 * beyond going first in the queue.
 */
function PersonOffer({
  support,
  urgent,
  onUrgent,
  busy,
  error,
  onAsk,
  onDismiss,
}: {
  support: HelpSupport;
  urgent: boolean;
  onUrgent: (value: boolean) => void;
  busy: boolean;
  error: string | null;
  onAsk: () => void;
  onDismiss?: () => void;
}) {
  const closed = support.onCall === 'after_hours';
  const fallback = `If nobody picks it up within ${HANDOFF_WAIT_MINUTES} minutes, you can raise a ticket instead.`;
  return (
    <div className="hc-person">
      <p className="hc-person-note">
        {!closed
          ? 'Someone from the LimoSphere team joins this conversation and answers you here.'
          : support.nextOpenAt
            ? `Our support desk is closed right now — it opens ${opensAt(support.nextOpenAt)} your time. You can still ask. ${fallback}`
            : `Our support desk isn't taking live chats right now. You can still ask. ${fallback}`}
      </p>
      <label className="hc-urgent">
        <input type="checkbox" checked={urgent} onChange={(e) => onUrgent(e.target.checked)} />
        It's urgent — put it first in the queue
      </label>
      <div className="hc-person-actions">
        <button type="button" className="hc-person-btn" disabled={busy} onClick={onAsk}>
          <Headset size={14} />
          {busy ? 'Asking…' : closed ? 'Ask anyway' : 'Talk to a person'}
        </button>
        {onDismiss && (
          <button type="button" className="hc-person-dismiss" onClick={onDismiss}>
            Not now
          </button>
        )}
      </div>
      {error && <p className="hc-support-failed">{error}</p>}
    </div>
  );
}

/**
 * Above the composer while a person is asked for or present: who the operator
 * is talking to, and the one way out. It is what says the next message goes to
 * a person rather than the assistant.
 */
function HandoffStrip({ handoff, busy, onEnd }: { handoff: HelpHandoff; busy: boolean; onEnd: () => void }) {
  const live = handoff.status === 'with_staff';
  return (
    <div className={live ? 'hc-handoff hc-handoff-live' : 'hc-handoff'} role="status">
      {live ? <Headset size={14} /> : <Hourglass size={14} />}
      <span className="hc-handoff-text">
        {live ? (
          <>
            You're talking to <strong>{handoff.staffName || 'LimoSphere Support'}</strong>
          </>
        ) : (
          <>
            Waiting for LimoSphere support
            {handoff.waitingSince ? ` · asked ${relativeTime(handoff.waitingSince)}` : ''}
            {handoff.urgency === 'urgent' ? ' · urgent' : ''}
          </>
        )}
      </span>
      <button type="button" className="hc-handoff-end" disabled={busy} onClick={onEnd}>
        {live ? 'End chat' : 'Cancel'}
      </button>
    </div>
  );
}

function SourceCards({
  sources,
  onOpen,
  onEnlarge,
}: {
  sources: HelpSource[];
  onOpen: (source: HelpSource) => void;
  onEnlarge: (source: HelpSource) => void;
}) {
  if (!sources.length) return null;
  return (
    <div className="hc-sources">
      {sources.map((source) => {
        // The module's glyph, so a stack of cards is scannable by shape before
        // it is read. Only shown when there is no screenshot to show instead.
        const Icon = moduleIcon(source.module);
        return (
          // A row, not one big button: the thumbnail and the title do different
          // things, and nesting a button inside a button is invalid anyway.
          <div key={source.id} className="hc-source">
            {source.imageUrl ? (
              <button
                type="button"
                className="hc-source-thumb"
                title="Enlarge this screenshot"
                aria-label={`Enlarge the screenshot for ${source.title}`}
                onClick={() => onEnlarge(source)}
              >
                <img src={source.imageUrl} alt="" loading="lazy" />
                <span className="hc-source-zoom" aria-hidden="true">
                  <Maximize2 size={12} />
                </span>
              </button>
            ) : (
              <span className="hc-source-glyph" aria-hidden="true">
                <Icon size={15} />
              </span>
            )}
            <button type="button" className="hc-source-open" onClick={() => onOpen(source)}>
              <span className="hc-source-title">{source.title}</span>
              <span className="hc-source-path">{source.navPath || source.module}</span>
            </button>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Shown under an answer the assistant escalated — i.e. one where the model
 * CALLED the escalate tool, never where it merely sounded unsure. The contact
 * details come from the server rather than being typed here, so the panel and
 * the ticket mail can never disagree about how to reach support.
 */
function SupportCard({
  support,
  ticket,
  canRaise,
  onRaise,
  personOffer = null,
}: {
  support: HelpSupport;
  ticket: TicketState;
  /**
   * False before there is a saved conversation, AND while a person is in this
   * one — mailing the desk the same question would open a second, unlinked
   * thread of it. It comes back if nobody picks the request up.
   */
  canRaise: boolean;
  onRaise: () => void;
  /** "Talk to a person", first because it is the quickest answer — when offered. */
  personOffer?: ReactNode;
}) {
  return (
    <div className="hc-support">
      <div className="hc-support-head">
        <LifeBuoy size={14} />
        <span>Talk to a human</span>
      </div>
      {personOffer}
      <div className="hc-support-actions">
        <a className="hc-support-btn" href={`mailto:${support.email}`}>
          <Mail size={14} />
          <span>
            <strong>Email us</strong>
            <em>{support.email}</em>
          </span>
        </a>
        <a className="hc-support-btn" href={`tel:${support.phoneHref}`}>
          <Phone size={14} />
          <span>
            <strong>Call us</strong>
            <em>{support.phone}</em>
          </span>
        </a>
      </div>

      {support.canRaiseTicket &&
        // Raising needs a saved conversation to summarise; before the first
        // answer lands there is nothing to send.
        canRaise &&
        (ticket.state === 'sent' ? (
          <p className="hc-support-done">
            Ticket raised — our team will try to get back to you within 24 hours.
            {ticket.acknowledgedTo ? ` A copy is on its way to ${ticket.acknowledgedTo}.` : ''}
          </p>
        ) : (
          <>
            <button
              type="button"
              className="hc-support-ticket"
              disabled={ticket.state === 'sending'}
              onClick={onRaise}
            >
              {ticket.state === 'sending' ? 'Sending…' : 'Raise a ticket'}
            </button>
            <p className="hc-support-hint">
              Sends this conversation to our support team, summarised. You'll get a copy by email.
            </p>
            {ticket.state === 'error' && <p className="hc-support-failed">{ticket.message}</p>}
          </>
        ))}
    </div>
  );
}

/**
 * The screenshot at panel width. Thumbnails are unreadable, and the old
 * behaviour — click and get navigated to the handbook — lost the answer you
 * were reading. "Open full size" is the escape hatch for the real resolution,
 * since the panel is only ~380px wide wherever it is hosted.
 */
function Lightbox({ source, onClose }: { source: HelpSource; onClose: () => void }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="hc-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Screenshot: ${source.title}`}
      onClick={onClose}
    >
      {/* The frame swallows clicks so only the backdrop dismisses. */}
      <div className="hc-lightbox-frame" onClick={(event) => event.stopPropagation()}>
        <div className="hc-lightbox-head">
          <span>{source.title}</span>
          <button type="button" aria-label="Close the screenshot" onClick={onClose}>
            <X size={15} />
          </button>
        </div>
        {source.imageUrl && <img src={source.imageUrl} alt={source.title} />}
        <div className="hc-lightbox-foot">
          {source.imageUrl && (
            <a href={source.imageUrl} target="_blank" rel="noopener noreferrer">
              Open full size
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/** The panel's landing view: what to ask here, and what was asked before. */
function HomeView({
  screen,
  questions,
  threads,
  onAsk,
  onOpen,
  onDelete,
}: {
  screen: string | null;
  questions: string[];
  threads: HelpThread[];
  onAsk: (question: string) => void;
  onOpen: (thread: HelpThread) => void;
  onDelete: (id: string) => void;
}) {
  // A conversation with a person open is the one thing on this screen that is
  // waiting on the operator, so it goes above everything else.
  const live = threads.find((thread) => thread.handoffStatus && thread.handoffStatus !== 'bot');
  return (
    <div className="hc-home">
      {live && (
        <button type="button" className="hc-live-banner" onClick={() => onOpen(live)}>
          <Headset size={14} />
          <span>
            {live.handoffStatus === 'with_staff'
              ? 'LimoSphere support is in your conversation'
              : 'You asked LimoSphere support for help'}
            <em>{live.title}</em>
          </span>
          <strong>Open</strong>
        </button>
      )}
      <p className="hc-home-intro">
        {/* The wave is the whole greeting — decorative, so it is hidden from
            screen readers rather than read out as "waving hand". */}
        <span className="hc-wave" aria-hidden="true">
          👋
        </span>
        Ask about the dispatch dashboard, the CRM, the partner portal or the chauffeur app — answers
        come from the product handbook, with the articles attached.
      </p>

      {questions.length > 0 && (
        <>
          <h3 className="hc-home-head">
            <Sparkles size={13} />
            {screen ? `On ${screen}` : 'Common questions'}
          </h3>
          <div className="hc-chips">
            {questions.map((question) => (
              <button type="button" key={question} className="hc-chip" onClick={() => onAsk(question)}>
                {question}
              </button>
            ))}
          </div>
        </>
      )}

      {threads.length > 0 && (
        <>
          <h3 className="hc-home-head">
            <MessageCircleQuestion size={13} />
            Earlier questions
          </h3>
          <div className="hc-threads">
            {threads.map((thread) => (
              <div key={thread.id} className="hc-thread-row">
                <button type="button" className="hc-thread-open" onClick={() => onOpen(thread)}>
                  <span className="hc-thread-title">{thread.title}</span>
                  <span className="hc-thread-meta">
                    {thread.handoffStatus === 'with_staff' && <span className="hc-live-tag">With support</span>}
                    {thread.handoffStatus === 'waiting' && <span className="hc-live-tag">Waiting for support</span>}
                    {thread.screen ? `${thread.screen} · ` : ''}
                    {relativeTime(thread.updatedAt)}
                  </span>
                </button>
                <button
                  type="button"
                  className="hc-thread-delete"
                  aria-label={`Delete "${thread.title}"`}
                  title="Delete"
                  onClick={() => onDelete(thread.id)}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <p className="hc-home-foot">Type below to start a new question.</p>
    </div>
  );
}

export function HelpChatPanel({
  routeContext = null,
  pageContext = null,
  onOpenArticle,
}: {
  /** The product screen the operator is on (dashboard embed forwards it). */
  routeContext?: string | null;
  /** What that screen chose to publish about its own state — see pageContext.ts. */
  pageContext?: HelpPageSnapshot | null;
  /** How to open a handbook path; defaults to a same-origin new tab. */
  onOpenArticle?: (handbookPath: string) => void;
}) {
  const [threads, setThreads] = useState<HelpThread[]>([]);
  const [active, setActive] = useState<HelpThread | null>(null);
  const [messages, setMessages] = useState<HelpMessage[]>([]);
  /**
   * Which half of the panel is on screen. EXPLICIT rather than derived from
   * `active`, because the thread id only exists once the server has answered —
   * deriving it meant a new question sat on the home view with a disabled
   * button and no sign anything was happening until the answer landed.
   */
  const [view, setView] = useState<'home' | 'thread'>('home');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** The question a failed send was carrying, so "Try again" has something to resend. */
  const [failed, setFailed] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState<HelpSource | null>(null);
  const [fetchedSuggestion, setFetchedSuggestion] = useState<Suggestion>(NO_SUGGESTION);
  const [support, setSupport] = useState<HelpSupport | null>(null);
  const [canAttach, setCanAttach] = useState(false);
  /** Uploaded screenshots waiting on a question to carry them. */
  const [shots, setShots] = useState<HelpAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const emojiRef = useDismissOnOutside<HTMLDivElement>(emojiOpen, () => setEmojiOpen(false));
  const [ticket, setTicket] = useState<TicketState>({ state: 'idle' });
  /** The open request for a person on the active thread — null when the assistant answers. */
  const [handoff, setHandoff] = useState<HelpHandoff | null>(null);
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [handoffError, setHandoffError] = useState<string | null>(null);
  const [urgent, setUrgent] = useState(false);
  /** "Talk to a person" asked for from the thread bar, not from an escalation. */
  const [offerOpen, setOfferOpen] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Read by the tail poll, which must ask "after the newest row I HAVE" — not
  // after the newest row its closure happened to see when it was scheduled.
  const messagesRef = useRef<HelpMessage[]>(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const suggestion = fetchedSuggestion.route === routeContext ? fetchedSuggestion : NO_SUGGESTION;

  // The operator's own conversations, from the server — so they are the same
  // ones in the console, in the dashboard panel and on another machine.
  const refreshThreads = useCallback(() => {
    listHelpThreads()
      .then(setThreads)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    refreshThreads();
  }, [refreshThreads]);

  // How to reach a human, fetched here rather than passed in: the panel has two
  // hosts and only one of them gates on /help/status, so relying on a prop
  // would leave the dashboard embed with no support card at all.
  useEffect(() => {
    let cancelled = false;
    getHelpStatus()
      .then((next) => {
        if (cancelled) return;
        if (next.support) setSupport(next.support);
        setCanAttach(!!next.canAttach);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Opening questions for wherever the operator is now. A failure just leaves
  // the chips off — this decorates the panel, it must never block it.
  useEffect(() => {
    if (!routeContext) return;
    let cancelled = false;
    getHelpSuggestions(routeContext)
      .then((next) => {
        if (!cancelled) setFetchedSuggestion({ route: routeContext, ...next });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [routeContext]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy, error]);

  useEffect(() => {
    if (!busy) return;
    const timers = STAGES.map(([delay, label]) => setTimeout(() => setStage(label), delay));
    return () => {
      for (const timer of timers) clearTimeout(timer);
    };
  }, [busy]);

  /**
   * The tail poll, while a person is asked for or present. The server sets the
   * pace (`nextPollMs`) and says when to stop (0, or no request any more —
   * closed, expired, or ended from the other side). It keeps running while the
   * panel is hidden: both hosts keep this component mounted, and a reply that
   * only arrived once the operator reopened the panel would be a reply nobody
   * saw. A failed poll retries, slower, rather than going quiet.
   */
  const polledThread = handoff && active ? active.id : null;
  useEffect(() => {
    if (!polledThread) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        const res = await pollHelpThread(polledThread, lastStoredId(messagesRef.current));
        if (cancelled) return;
        if (res.messages.length) setMessages((current) => mergeMessages(current, res.messages));
        setHandoff(res.handoff);
        if (!res.handoff || !res.nextPollMs) {
          // The person has gone; the list's "with support" tag has to go too.
          refreshThreads();
          return;
        }
        timer = setTimeout(() => void tick(), res.nextPollMs);
      } catch {
        if (!cancelled) timer = setTimeout(() => void tick(), POLL_RETRY_MS);
      }
    };
    timer = setTimeout(() => void tick(), 1500);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [polledThread, refreshThreads]);

  const openArticle = (source: HelpSource) => {
    if (onOpenArticle) onOpenArticle(source.handbookPath);
    else window.open(source.handbookPath, '_blank', 'noopener');
  };

  const openThread = async (thread: HelpThread) => {
    setActive(thread);
    setView('thread');
    setError(null);
    setFailed(null);
    setTicket({ state: 'idle' });
    setMessages([]);
    setHandoff(null);
    setHandoffError(null);
    setOfferOpen(false);
    try {
      const full = await getHelpThread(thread.id);
      setActive(full);
      setMessages(full.messages);
      // Rehydrates a conversation a person is in, after a reload or from the list.
      setHandoff(full.handoff ?? null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not open that conversation.');
    }
  };

  const dropShot = (key: string) =>
    setShots((current) =>
      current.filter((shot) => {
        if (shot.key !== key) return true;
        if (shot.url) URL.revokeObjectURL(shot.url);
        return false;
      }),
    );

  /**
   * Screenshots go to S3 as they are picked, so the question that carries them
   * is still just JSON. `url` here is a LOCAL object url — the stored copy comes
   * back presigned with the answer and replaces it.
   */
  const pickShots = async (picked: FileList | null) => {
    if (!picked?.length) return;
    setError(null);
    setUploading(true);
    try {
      let held = shots.length;
      for (const file of Array.from(picked)) {
        if (held >= HELP_MAX_ATTACHMENTS) {
          setError(`Up to ${HELP_MAX_ATTACHMENTS} screenshots per question.`);
          break;
        }
        if (file.size > HELP_UPLOAD_MAX_BYTES) {
          setError(`${file.name} is larger than 5 MB.`);
          continue;
        }
        try {
          const { s3Key, putUrl } = await presignHelpUpload(file.name, file.type);
          await putToS3(putUrl, file);
          held += 1;
          setShots((current) => [
            ...current,
            {
              key: s3Key,
              fileName: file.name,
              contentType: file.type,
              fileSize: file.size,
              url: URL.createObjectURL(file),
            },
          ]);
        } catch (e) {
          setError(e instanceof ApiError ? e.message : `Could not attach ${file.name}.`);
        }
      }
    } finally {
      setUploading(false);
      // Picking the same file twice in a row fires no change event otherwise.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const send = async (question: string) => {
    const content = question.trim();
    if (!content || busy || uploading) return;
    const carried = shots;
    setDraft('');
    setBusy(true);
    setError(null);
    setFailed(null);
    setEmojiOpen(false);
    // Into the thread IMMEDIATELY — before the request, not after it resolves.
    // The optimistic question and the staged waiting line are the whole answer
    // to "did that button do anything?".
    setView('thread');
    // Optimistic: the question appears immediately, the answer follows.
    const pending = pendingMessage(content, carried);
    setMessages((current) => [...current, pending]);
    try {
      const context =
        routeContext || pageContext
          ? { ...(routeContext ? { route: routeContext } : {}), ...(pageContext ? { page: pageContext } : {}) }
          : undefined;
      const res = await sendHelpChat(
        content,
        active?.id ?? null,
        context,
        carried.map((shot) => ({ key: shot.key })),
      );
      setActive(res.thread);
      // The server's copy of the question replaces the optimistic one, so the
      // screenshots stop depending on object urls this tab happens to hold.
      // Functional, because a person's reply may have been polled in while
      // this was on its way — the list this call started from is stale.
      setMessages((current) =>
        mergeMessages(
          current.filter((message) => message.id !== pending.id),
          [res.question ?? pending, ...(res.message ? [res.message] : [])],
        ),
      );
      // A turn a person took says so. An ANSWER means the assistant took it,
      // so any request this panel thought was open closed on the way.
      if (res.handoff !== undefined) setHandoff(res.handoff);
      else if (res.message) setHandoff(null);
      for (const shot of carried) if (shot.url) URL.revokeObjectURL(shot.url);
      setShots([]);
      refreshThreads();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong — try again.');
      // Drop the optimistic turn: the server never stored it, so leaving it on
      // screen would promise a question that was never asked. Held in `failed`
      // instead, which is what makes "Try again" able to resend it.
      setFailed(content);
      setMessages((current) => current.filter((message) => message.id !== pending.id));
    } finally {
      setBusy(false);
      setStage('');
    }
  };

  const startNew = () => {
    setActive(null);
    setView('home');
    setMessages([]);
    setError(null);
    setFailed(null);
    setTicket({ state: 'idle' });
    // The request stays open on the server; the home view's banner leads back.
    setHandoff(null);
    setHandoffError(null);
    setOfferOpen(false);
    setDraft('');
    for (const shot of shots) if (shot.url) URL.revokeObjectURL(shot.url);
    setShots([]);
    setEmojiOpen(false);
    inputRef.current?.focus();
  };

  const remove = async (id: string) => {
    setThreads((current) => current.filter((row) => row.id !== id));
    if (active?.id === id) startNew();
    try {
      await deleteHelpThread(id);
    } catch {
      refreshThreads();
    }
  };

  const retry = () => {
    if (busy || !failed) return;
    setError(null);
    void send(failed);
  };

  /**
   * "Talk to a person". Only ever this click queues one — the assistant can
   * surface the button, never press it.
   */
  const askForPerson = async () => {
    if (!active || handoffBusy) return;
    setHandoffBusy(true);
    setHandoffError(null);
    try {
      const context =
        routeContext || pageContext
          ? { ...(routeContext ? { route: routeContext } : {}), ...(pageContext ? { page: pageContext } : {}) }
          : undefined;
      const res = await requestHelpHandoff(active.id, {
        urgency: urgent ? 'urgent' : 'normal',
        ...(context ? { context } : {}),
      });
      setHandoff(res.handoff);
      const notice = res.message;
      if (notice) setMessages((current) => mergeMessages(current, [notice]));
      if (res.support) setSupport(res.support);
      setOfferOpen(false);
      setUrgent(false);
      refreshThreads();
    } catch (e) {
      // A conflict names a conversation already open with support; the list's
      // banner is the way to it once the operator steps back.
      setHandoffError(e instanceof ApiError ? e.message : 'Could not reach the support team — try again.');
      if (e instanceof ApiError && e.kind === 'conflict') refreshThreads();
    } finally {
      setHandoffBusy(false);
    }
  };

  /** "Cancel" while waiting, "End chat" with a person — the assistant answers next. */
  const endHandoff = async () => {
    if (!active || !handoff || handoffBusy) return;
    setHandoffBusy(true);
    setHandoffError(null);
    try {
      const res = await cancelHelpHandoff(active.id);
      setHandoff(null);
      const notice = res.message;
      if (notice) setMessages((current) => mergeMessages(current, [notice]));
      refreshThreads();
    } catch (e) {
      setHandoffError(e instanceof ApiError ? e.message : 'Could not end that — try again.');
    } finally {
      setHandoffBusy(false);
    }
  };

  const raise = async () => {
    if (!active || ticket.state === 'sending') return;
    setTicket({ state: 'sending' });
    try {
      const res = await raiseHelpTicket(active.id);
      setTicket({ state: 'sent', acknowledgedTo: res.acknowledgedTo });
    } catch (e) {
      // The message is the server's: it names the support email and phone when
      // the send itself failed, which is exactly what to do next.
      setTicket({
        state: 'error',
        message: e instanceof ApiError ? e.message : 'Could not raise that ticket — please email us.',
      });
    }
  };

  // The thread was started somewhere else, so its history is about that screen.
  const movedOn = !!active && !!active.route && !!routeContext && active.route !== routeContext;
  const inThread = view === 'thread';
  // The last answer the assistant escalated — or the desk's notice that nobody
  // picked a request up, which brings the same card back. Only the newest one
  // carries it: an old escalation the operator has since moved past is
  // history, and stacking a support box under every one buries the thread.
  const lastUnresolved = [...messages]
    .reverse()
    .find((m) => m.unresolved && (m.role === 'assistant' || m.role === 'system'));
  // Offered once the deployment has a way for a person to answer, on a saved
  // conversation, when nobody has been asked for yet.
  const canAskPerson = !!support?.canHandoff && !!active && !handoff;
  // The support card at the foot of the thread already carries the button; a
  // second one in the bar would be two ways to do one thing on one screen.
  const cardAtFoot = !!lastUnresolved && messages[messages.length - 1]?.id === lastUnresolved.id;
  const personOffer = (onDismiss?: () => void) =>
    support && canAskPerson ? (
      <PersonOffer
        support={support}
        urgent={urgent}
        onUrgent={setUrgent}
        busy={handoffBusy}
        error={handoffError}
        onAsk={() => void askForPerson()}
        onDismiss={onDismiss}
      />
    ) : null;

  return (
    <div className="hc-panel">
      {inThread && (
        <div className="hc-thread-bar">
          <button type="button" className="hc-back" onClick={startNew}>
            <ChevronLeft size={14} />
            All questions
          </button>
          <span className="hc-thread-bar-title">
            {active?.screen ?? suggestion.screen ?? 'Question'}
          </span>
          {/* Not only after an escalation: an operator who already knows they
              need a person should not have to talk the assistant into it. */}
          {canAskPerson && !cardAtFoot && (
            <button
              type="button"
              className="hc-person-open"
              aria-expanded={offerOpen}
              onClick={() => setOfferOpen((value) => !value)}
            >
              <Headset size={13} />
              Talk to a person
            </button>
          )}
          <button type="button" className="hc-new" onClick={startNew}>
            <Plus size={13} />
            New
          </button>
        </div>
      )}

      <div className="hc-messages" ref={listRef}>
        {!inThread ? (
          <HomeView
            screen={suggestion.screen}
            questions={suggestion.questions}
            threads={threads}
            onAsk={(question) => void send(question)}
            onOpen={(thread) => void openThread(thread)}
            onDelete={(id) => void remove(id)}
          />
        ) : (
          <>
            {movedOn && (
              <div className="hc-moved">
                This conversation started on <strong>{active?.screen ?? active?.route}</strong>. You are
                somewhere else now —{' '}
                <button type="button" className="hc-retry" onClick={startNew}>
                  ask a new question
                </button>{' '}
                to get answers about this screen.
              </div>
            )}
            {messages.map((message) => {
              const card =
                support && message.id === lastUnresolved?.id ? (
                  <SupportCard
                    support={support}
                    ticket={ticket}
                    canRaise={!!active && !handoff}
                    onRaise={() => void raise()}
                    personOffer={personOffer()}
                  />
                ) : null;
              // The desk's own line ("someone has joined") — not a speaker, so
              // not a bubble.
              if (message.role === 'system') {
                return (
                  <div key={message.id} className="hc-turn hc-notice-turn">
                    <p className="hc-notice">{message.content}</p>
                    {card}
                  </div>
                );
              }
              const side =
                message.role === 'user' ? 'hc-user' : message.role === 'staff' ? 'hc-staff' : 'hc-assistant';
              return (
              <div key={message.id} className={`hc-turn ${side}`}>
                {message.role === 'staff' && (
                  <span className="hc-author">
                    <Headset size={11} />
                    {message.author || 'LimoSphere Support'}
                  </span>
                )}
                <div className="hc-bubble">
                  {/* A person's words are shown as typed: markdown is the
                      assistant's dialect, and a stray asterisk from a human
                      should not turn half a reply into italics. */}
                  {message.role === 'assistant' ? renderMarkdown(message.content) : message.content}
                  {(message.attachments || []).length > 0 && (
                    <div className="hc-shots">
                      {(message.attachments || []).map((shot) =>
                        shot.url ? (
                          <a
                            key={shot.key}
                            href={shot.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={shot.fileName}
                          >
                            <img src={shot.url} alt={shot.fileName} />
                          </a>
                        ) : (
                          // A url that could not be signed still says what was
                          // sent, which beats a gap where a screenshot was.
                          <span key={shot.key} className="hc-shot-missing">
                            {shot.fileName}
                          </span>
                        ),
                      )}
                    </div>
                  )}
                </div>
                {message.role === 'assistant' && message.sources.length > 0 && (
                  <SourceCards sources={message.sources} onOpen={openArticle} onEnlarge={setZoomed} />
                )}
                {card}
              </div>
              );
            })}
            {offerOpen && canAskPerson && !cardAtFoot && (
              <div className="hc-turn hc-assistant">{personOffer(() => setOfferOpen(false))}</div>
            )}
            {/* The staged "Thinking…" is the assistant's; a message to a person
                is simply sent, and their reply arrives when it arrives. */}
            {busy && !handoff && (
              <div className="hc-turn hc-assistant">
                <div className="hc-bubble hc-stage">
                  <span className="hc-dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  {stage || 'Thinking…'}
                </div>
              </div>
            )}
            {error && (
              <div className="hc-error">
                {error}{' '}
                {failed && (
                  <button type="button" className="hc-retry" onClick={retry}>
                    Try again
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {inThread && handoff && (
        <>
          <HandoffStrip handoff={handoff} busy={handoffBusy} onEnd={() => void endHandoff()} />
          {handoffError && <p className="hc-handoff-error">{handoffError}</p>}
        </>
      )}
      {shots.length > 0 && (
        <div className="hc-pending">
          {shots.map((shot) => (
            <span key={shot.key} className="hc-pending-shot">
              {shot.url && <img className="hc-pending-thumb" src={shot.url} alt="" />}
              <span className="hc-pending-name">{shot.fileName}</span>
              <button
                type="button"
                aria-label={`Remove ${shot.fileName}`}
                onClick={() => dropShot(shot.key)}
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="hc-composer">
        {/* Left of the box: the Ask button is the one control that must not
            move as the composer fills up. */}
        <div className="hc-tools">
          {canAttach && (
            <>
              <button
                type="button"
                className="hc-iconbtn"
                title={uploading ? 'Uploading…' : 'Attach a screenshot'}
                aria-label={uploading ? 'Uploading…' : 'Attach a screenshot'}
                disabled={busy || uploading || shots.length >= HELP_MAX_ATTACHMENTS}
                onClick={() => fileInputRef.current?.click()}
              >
                <ImagePlus size={15} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept={HELP_UPLOAD_ACCEPT}
                onChange={(e) => void pickShots(e.target.files)}
              />
            </>
          )}
          <button
            type="button"
            className="hc-iconbtn"
            title="Emoji"
            aria-label="Emoji"
            aria-expanded={emojiOpen}
            onClick={() => setEmojiOpen((current) => !current)}
          >
            <Smile size={15} />
          </button>
          {emojiOpen && (
            <div className="hc-emoji" ref={emojiRef}>
              {EMOJI.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => {
                    setDraft((current) => current + emoji);
                    setEmojiOpen(false);
                    inputRef.current?.focus();
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
        <textarea
          ref={inputRef}
          className="hc-input"
          rows={1}
          placeholder={
            handoff
              ? handoff.status === 'with_staff'
                ? 'Reply to LimoSphere Support…'
                : 'Add anything the support team should know…'
              : inThread
                ? 'Ask a follow-up…'
                : 'Ask the assistant…'
          }
          value={draft}
          maxLength={4000}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send(draft);
            }
          }}
        />
        {/* "Ask" opens a conversation; inside one the same button is a reply,
            and saying so is what keeps the follow-up placeholder honest. */}
        <button
          type="button"
          className="hc-send"
          disabled={busy || uploading || !draft.trim()}
          onClick={() => void send(draft)}
        >
          {handoff ? 'Send' : inThread ? 'Reply' : 'Ask'}
        </button>
      </div>
      {zoomed && <Lightbox source={zoomed} onClose={() => setZoomed(null)} />}
    </div>
  );
}
